import { useState, useEffect } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  DevHubBox,
  DevHubTypography,
  DevHubChip,
  DevHubColorProps,
  DevHubPaper,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { CopyIdButton } from '../onboarding/experimentation/components/CopyIdButton';

type MethodState =
  | 'pending'
  | 'in-progress'
  | 'completed'
  | 'failed'
  | 'skipped';

type StatusEvent = {
  state: MethodState;
  stateKey: string;
  methodName: string;
  startTime?: number;
  endTime?: number;
  duration?: number;
  error?: string;
};

interface RefreshStatusLogProps {
  runId: string | null;
}

/**
 * RefreshStatusLog component
 * @param props - Component props
 * @param props.runId - The run ID for the refresh
 * @constructor
 */
export const RefreshStatusLog = (props: RefreshStatusLogProps) => {
  const { runId } = props;
  const config = useApi(configApiRef);
  const [events, setEvents] = useState<StatusEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Adjust the PrimeReact theme based on the theme stored in local storage
  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const backendUrl = config.getString('backend.baseUrl');
        const response = await fetch(
          `${backendUrl}/api/fusionUtil/status/${runId}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch status: ${response.statusText}`);
        }

        const data = await response.json();
        setEvents(data?.executions || []);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    if (runId) {
      fetchStatus();
    }
  }, [config, runId]);

  const getStatusColor = (state: MethodState): DevHubColorProps => {
    switch (state) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'in-progress':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'skipped':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDuration = (duration?: number): string => {
    if (!duration) return '-';
    return `${(duration / 1000).toFixed(2)}s`;
  };

  if (!runId) {
    return null;
  }

  if (loading) {
    return (
      <DevHubBox display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </DevHubBox>
    );
  }

  if (error) {
    return (
      <DevHubBox p={2}>
        <DevHubTypography color="error">
          Error loading status: {error}
        </DevHubTypography>
      </DevHubBox>
    );
  }

  if (events && events.length === 0) {
    return (
      <DevHubBox p={2}>
        <DevHubTypography>No status events found</DevHubTypography>
      </DevHubBox>
    );
  }

  const statusBodyTemplate = (rowData: StatusEvent) => {
    return (
      <DevHubBox display="flex" justifyContent="center">
        <DevHubChip
          label={rowData.state}
          chipColor={getStatusColor(rowData.state)}
          chipSize="small"
        />
      </DevHubBox>
    );
  };

  const durationBodyTemplate = (rowData: StatusEvent) => {
    return formatDuration(rowData.duration);
  };

  const errorBodyTemplate = (rowData: StatusEvent) => {
    return rowData.error || '-';
  };

  return (
    <DevHubPaper>
      <DevHubBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <DevHubTypography variant="h5">Refresh Status Log</DevHubTypography>
        <DevHubBox display="flex" alignItems="center">
          <DevHubTypography
            variant="body2"
            color="textSecondary"
            style={{ marginRight: 4 }}
          >
            Run ID:
          </DevHubTypography>
          <DevHubTypography variant="body2" color="textSecondary">
            {runId}
          </DevHubTypography>
          <CopyIdButton value={runId} ariaLabel="Copy Run ID" />
        </DevHubBox>
      </DevHubBox>
      <div className="card">
        <DataTable
          value={events}
          className="p-datatable-striped"
          showGridlines
          loading={loading}
          emptyMessage="No status events found"
          sortMode="single"
          removableSort
        >
          <Column
            field="methodName"
            header="Method"
            sortable
            style={{ width: '40%' }}
          />
          <Column
            field="state"
            header="Status"
            body={statusBodyTemplate}
            sortable
            style={{ width: '15%', textAlign: 'center' }}
          />
          <Column
            field="duration"
            header="Duration"
            body={durationBodyTemplate}
            sortable
            style={{ width: '15%' }}
          />
          <Column
            field="error"
            header="Error"
            body={errorBodyTemplate}
            sortable
            style={{ width: '30%' }}
          />
        </DataTable>
      </div>
    </DevHubPaper>
  );
};
